import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import { billingApi } from '../services/api';
import { useAccountPlan, useAccountUsage } from '../hooks/useAccountUsage';
import { useSubscriptionSummary } from '../hooks/useSubscriptionSummary';
import { useInvoices, type InvoiceSummary } from '../hooks/useInvoices';
import { useFlag } from '../hooks/useFeatureFlags';
import { keyScopeSpend, useAccountModelProviders } from '../hooks/useAccountModels';
import SpendingBar from '../components/ui/SpendingBar';
import { SPENDING_PAGE_PATH, formatBilledCents, formatSpendingCents, spendingReading } from '../utils/spending';

/**
 * SettingsBillingPage — Settings → Billing → Billing (task 630).
 *
 * Billing answers a single question: *what does this cost?* The headline is an
 * itemized current-period table whose lines each show their arithmetic and
 * visibly sum to an estimated total:
 *
 *   plan fee                                       (catalog basePriceCents)
 * + extra endpoints  max(0, deployed − included) × extra_endpoint_price_cents
 * + metered usage    (rawCostMicroUsd / 1e6) × (model_cost_markup_bps / 10000)
 * ─────────────────────────────────────────────────────────────────────────
 * = estimated total so far
 *
 * Billing truth is Stripe (tasks 942/963/964). The plan line names the plan
 * Stripe CHARGES and prices it from the Stripe subscription item
 * (`billedPlan` on GET /api/billing/subscription), and the period header is the
 * Stripe billing period (`currentPeriodStart` – `currentPeriodEnd`), which is an
 * anniversary window (Sep 8 – Oct 8), not the calendar month. The AMS
 * entitlement plan, when it deliberately differs, is marked under the table
 * rather than passed off as the billed one. The catalog base fee and the
 * calendar-month label are the FALLBACKS, for when that read is absent (not
 * deployed, not an owner, no Stripe customer).
 *
 * The other inputs are the ones the platform already exposes — the
 * current-period usage summary (GET /api/accounts/usage, which also carries the
 * plan slug and its limits) and the AMS plan catalog (GET /api/billing/plans).
 *
 * Metered LLM usage stays keyed by CALENDAR MONTH (innovate-service
 * `processes/usageMetering.ts`) and is not pushed to Stripe today — only
 * endpoint extras become Stripe subscription items. Its row therefore names its
 * own window so the Stripe period header above it cannot imply the metered
 * figure is period-aligned.
 *
 * Below the table: the endpoint-allowance meter (indigo within allowance, amber
 * over, with the "undeploying stops the charge next period" note) and the
 * invoice-history table from the task-627 invoice-list read. That read is not
 * yet deployed; the invoices card degrades gracefully (hidden on 403/404/501)
 * exactly like the sibling Subscription/Payment pages, while the period
 * itemization always renders because its inputs already exist.
 *
 * Owner-only, like the rest of the Billing group. A non-owner who deep-links
 * here sees the managed-by-owner note and no account data is fetched.
 */

/** Convert basis-points markup (e.g. 12500) to a human multiplier (1.25). */
function markupMultiplier(bps: number | undefined): number | null {
  if (bps === undefined || bps < 0) return null;
  return bps / 10000;
}

function formatUsd(dollars: number): string {
  return `$${dollars.toFixed(2)}`;
}

function formatCentsAsUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Format a money amount in the smallest currency unit for display. */
function formatMoney(minorUnits: number, currency: string): string {
  const amount = minorUnits / 100;
  const code = (currency || 'usd').toUpperCase();
  if (code === 'USD') return `$${amount.toFixed(2)}`;
  return `${amount.toFixed(2)} ${code}`;
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** ISO date → "Aug 1, 2026". Falls back to the raw string if unparseable. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** ISO date → "Aug 1" (month + day), the short half of a same-year range. */
function formatMonthDay(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Render a pair of ISO bounds as one period label (task 964).
 *
 * A Stripe billing period is an anniversary window, not a calendar month, so
 * both ends are named: "Sep 8 – Oct 8, 2026", or "Dec 8, 2026 – Jan 8, 2027"
 * when the window straddles a year boundary. With only one bound known that
 * date stands alone; with neither, there is no label.
 */
function formatDateRange(start: string | null | undefined, end: string | null | undefined): string | null {
  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;
  const startValid = startDate !== null && !Number.isNaN(startDate.getTime());
  const endValid = endDate !== null && !Number.isNaN(endDate.getTime());

  if (startValid && endValid) {
    if ((startDate as Date).getFullYear() === (endDate as Date).getFullYear()) {
      return `${formatMonthDay(start as string)} – ${formatDate(end as string)}`;
    }
    return `${formatDate(start as string)} – ${formatDate(end as string)}`;
  }
  if (startValid) return formatDate(start as string);
  if (endValid) return formatDate(end as string);
  return null;
}

/**
 * The calendar month the usage counters are keyed by, expanded from the usage
 * summary's "YYYY-MM" `period` key to "Aug 1 – Aug 31, 2026". Any other shape is
 * rendered verbatim. This is the FALLBACK period label — it stands only when the
 * Stripe subscription period is unknown — and it is also what the metered-usage
 * row names as its own window, since LLM usage is aggregated per calendar month
 * regardless of where Stripe's anniversary falls.
 */
function formatCalendarMonthPeriod(period: string | undefined, withYear: boolean = true): string | null {
  if (!period) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1; // 0-based
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0); // last day of month
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return period;
  const monthName = start.toLocaleDateString('en-US', { month: 'short' });
  const range = `${monthName} ${start.getDate()} – ${monthName} ${end.getDate()}`;
  return withYear ? `${range}, ${year}` : range;
}

/**
 * The plan row's Detail cell, named for the interval Stripe actually bills.
 * Without a Stripe read the platform's only self-serve cadence is monthly, which
 * is what this page said before.
 */
function planFeeDetailLabel(interval: string | null | undefined): string {
  if (interval === 'year') return 'Yearly plan fee';
  if (interval === 'month' || !interval) return 'Monthly plan fee';
  return 'Plan fee';
}

/** Stripe invoice status → badge classes. */
function invoiceStatusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'paid':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300';
    case 'open':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300';
    case 'void':
    case 'uncollectible':
      return 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300';
    default: // draft and anything else
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }
}

function InvoiceStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${invoiceStatusBadgeClass(status)}`}
      data-testid="billing-invoice-status"
    >
      {titleCase(status)}
    </span>
  );
}

/**
 * The invoice Period column value (task 964): the date range the invoice covers.
 * Since task 963 those bounds come from the invoice's line items, which carry
 * the real Stripe service period — an anniversary window a month-year label
 * cannot express. One bound known renders that date; neither known falls back to
 * the invoice's created date.
 */
function invoicePeriodLabel(invoice: InvoiceSummary): string {
  return formatDateRange(invoice.periodStart, invoice.periodEnd) ?? formatDate(invoice.created);
}

export default function SettingsBillingPage() {
  // Billing affordances are gated by innovate.fe.billing.page, whose
  // role-owner cohort is the owner check (task 692). The role claim is no
  // longer read here: evaluation is uniform and, as a permission-type flag,
  // fails closed during an Unleash outage.
  const canManageBilling = useFlag('innovate.fe.billing.page');

  // Plan + limits come from GET /api/accounts/usage (task 676 — previously the
  // JWT `plan` / `limits` claims). No plan slug = no active subscription
  // (there is no free tier).
  const { plan, limits, isPending: planPending } = useAccountPlan();
  const planSlug = plan?.toLowerCase() || null;
  const hasNoPlan = !planSlug;

  const includedEndpoints = limits['max_endpoints']; // -1 = unlimited
  const hasFiniteEndpointCap =
    includedEndpoints !== undefined && includedEndpoints !== -1 && includedEndpoints >= 0;
  const extraEndpointPriceCents = limits['extra_endpoint_price_cents'];
  const markup = markupMultiplier(limits['model_cost_markup_bps']);

  // --- Current-period usage summary (GET /api/accounts/usage) ------------------
  // The shared query — it is also what supplies the plan and limits above, so
  // it can no longer be gated on already knowing the plan.
  const { data: usage } = useAccountUsage();

  // --- Spending limit (task 1159) ---------------------------------------------
  // The usage summary's `spending` block. With a limit set, the metered-usage
  // row names it and gains the bar below it; without one nothing changes.
  const spending = spendingReading(usage?.spending);
  const spendingPageEnabled = useFlag('innovate.fe.spending.page');

  // --- Plan catalog (task 232): current plan's base fee + display name --------
  const { data: catalog } = useQuery({
    queryKey: ['planCatalog'],
    enabled: !hasNoPlan,
    queryFn: async () => {
      const res = await billingApi.getPlans();
      return res.data.data.plans;
    },
    staleTime: 10 * 60_000,
    retry: 1,
  });

  // --- Stripe subscription summary (tasks 942/963, shared hook task 964) ------
  // The billed plan, the Stripe billing period and the entitlement face. Gated
  // on having a plan at all; resolves to null whenever the read is unavailable,
  // and every line below falls back to what it rendered before.
  const { data: summary } = useSubscriptionSummary(canManageBilling && !hasNoPlan);

  // --- Model usage split by key (task 1147; AMS task 1148 carries the figures) --
  // Informational sub-lines under the metered-usage row, from the summary's
  // `usage` (micro-USD strings): what ran on the platform key (what Innovate
  // charges) and what ran on the account's own keys (paid to the provider).
  // They explain the metered line; they are not added to the total. "Your key
  // usage" shows only when the account stores a key.
  const keySpend = keyScopeSpend(summary);
  const { data: modelProviders } = useAccountModelProviders();
  const hasAccountKey = (modelProviders ?? []).some((provider) => Boolean(provider.keyLastFour));

  // --- Invoice history (task 627 read; owner + plan only, degrades to null) ---
  const { data: invoices, isLoading: invoicesLoading, isError: invoicesError } =
    useInvoices(canManageBilling && !hasNoPlan);

  // --- Non-owner: managed by the account owner --------------------------------
  if (!canManageBilling) {
    return (
      <div className="space-y-6" data-testid="settings-billing-page">
        <PageHeader
          title="Billing"
          description="What your account is billed this period, and your past invoices."
        />
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="billing-owner-note">
          Billing is managed by your account owner.
        </p>
      </div>
    );
  }

  // Plan not known yet — hold the render rather than flash the no-plan panel
  // at an account that has a plan (task 676).
  if (planPending) return null;

  // --- No plan yet: nothing to itemize; point to Subscription -----------------
  if (hasNoPlan) {
    return (
      <div className="space-y-6" data-testid="settings-billing-page">
        <PageHeader
          title="Billing"
          description="What your account is billed this period, and your past invoices."
        />
        <section
          className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
          data-testid="billing-no-plan"
        >
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">No plan yet</h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Your account doesn't have a plan yet, so there's nothing to bill. Choose a plan to get
            started — your current-period breakdown and invoices appear here once you're subscribed.
          </p>
          <Link
            to="/settings/subscription"
            data-testid="billing-choose-plan-link"
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
          >
            Choose a plan
          </Link>
        </section>
      </div>
    );
  }

  // --- Itemization figures ----------------------------------------------------
  const currentPlan = catalog?.find((plan) => plan.slug === planSlug);
  const catalogPlanLabel = currentPlan?.name ?? titleCase(planSlug as string);

  // Billing truth is Stripe (task 942): this table itemizes what the account is
  // CHARGED, so the plan row names and prices the Stripe-billed plan whenever
  // the summary read answers. The AMS entitlement plan is marked under the
  // table instead of being priced as if it were billed.
  const billed = summary?.billedPlan ?? null;
  const entitlement = summary?.entitlementPlan ?? null;
  const isEntitlementOverride = entitlement?.isOverride === true;
  const billedLabel = billed?.name ?? (billed?.slug ? titleCase(billed.slug) : null);
  const planLabel = billedLabel ?? catalogPlanLabel;
  const planFeeDetail = planFeeDetailLabel(billed?.interval);

  // A custom-priced catalog plan (base 0, not self-serve, e.g. Enterprise) has
  // no published fee — the total is then "Custom" rather than a computed number.
  // That fallback only matters when Stripe has no amount to show.
  const planIsCustomPriced =
    currentPlan !== undefined && !currentPlan.selfServe && currentPlan.basePriceCents === 0;
  const catalogPlanFeeCents =
    currentPlan !== undefined && !planIsCustomPriced ? currentPlan.basePriceCents : null;
  const planFeeCents = billed?.amountCents ?? catalogPlanFeeCents;

  const deployedEndpoints = usage?.deployedEndpoints;
  const hasUsage = usage != null;
  const extraEndpoints =
    hasFiniteEndpointCap && deployedEndpoints !== undefined
      ? Math.max(0, deployedEndpoints - (includedEndpoints as number))
      : 0;
  const hasExtras =
    extraEndpoints > 0 && extraEndpointPriceCents !== undefined && extraEndpointPriceCents > 0;
  const extrasMonthlyCents = hasExtras ? extraEndpoints * (extraEndpointPriceCents as number) : 0;

  const rawUsageDollars =
    usage?.rawCostMicroUsd !== undefined ? usage.rawCostMicroUsd / 1_000_000 : undefined;
  const billedUsageDollars =
    rawUsageDollars !== undefined ? rawUsageDollars * (markup ?? 1) : undefined;
  const meteredUsageCents =
    billedUsageDollars !== undefined ? Math.round(billedUsageDollars * 100) : undefined;

  // Estimated total = every KNOWN numeric line. Computable only when the plan
  // fee is a real number (not a custom plan).
  const totalComputable = planFeeCents !== null;
  const estimatedTotalCents = totalComputable
    ? (planFeeCents as number) + extrasMonthlyCents + (meteredUsageCents ?? 0)
    : null;

  // The header names STRIPE's billing period; the calendar month the usage
  // counters are keyed by is the fallback for when those bounds are unknown.
  const calendarPeriodLabel = formatCalendarMonthPeriod(usage?.period);
  const stripePeriodLabel = formatDateRange(summary?.currentPeriodStart, summary?.currentPeriodEnd);
  const periodLabel = stripePeriodLabel ?? calendarPeriodLabel;
  const nextInvoiceDate = billed?.nextBillingDate ?? null;
  // The metered-usage row's own window: always the calendar month.
  const usageWindowLabel = formatCalendarMonthPeriod(usage?.period, false);

  // Meter fill: deployed vs included (capped at 100%).
  const meterPct = hasFiniteEndpointCap
    ? Math.min(100, ((deployedEndpoints ?? 0) / Math.max(1, includedEndpoints as number)) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="settings-billing-page">
      <PageHeader
        title="Billing"
        description="What your account is billed this period, and your past invoices. Payment is handled securely by Stripe."
      />

      {/* Current period — itemized running total */}
      <section
        className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
        data-testid="billing-current-period-card"
      >
        <div className="p-6 pb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Current period{periodLabel ? ` — ${periodLabel}` : ''}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            A running total of what this period will cost. Usage settles at period close; the final
            amount appears on your next invoice
            {nextInvoiceDate ? (
              <span data-testid="billing-next-invoice-date">, dated {formatDate(nextInvoiceDate)}</span>
            ) : null}
            .
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="billing-itemization-table">
            <thead>
              <tr className="border-y border-gray-200 bg-gray-50 text-left dark:border-gray-700 dark:bg-gray-900/40">
                <th className="px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Item
                </th>
                <th className="px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Detail
                </th>
                <th className="px-6 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Plan fee — always present for a subscribed account */}
              <tr className="border-b border-gray-200 dark:border-gray-700" data-testid="billing-line-plan">
                <td className="px-6 py-3 font-medium text-gray-900 dark:text-white">{planLabel} plan</td>
                <td className="px-6 py-3 text-gray-500 dark:text-gray-400">{planFeeDetail}</td>
                <td className="px-6 py-3 text-right tabular-nums text-gray-900 dark:text-white" data-testid="billing-line-plan-amount">
                  {planFeeCents !== null ? formatCentsAsUsd(planFeeCents) : 'Custom'}
                </td>
              </tr>

              {/* Extra endpoints — only when deployed exceeds the included cap */}
              {hasExtras && (
                <tr className="border-b border-gray-200 dark:border-gray-700" data-testid="billing-line-extras">
                  <td className="px-6 py-3 font-medium text-gray-900 dark:text-white">Extra endpoints</td>
                  <td className="px-6 py-3 text-gray-500 dark:text-gray-400">
                    {deployedEndpoints} deployed − {includedEndpoints as number} included ={' '}
                    {extraEndpoints} extra × {formatCentsAsUsd(extraEndpointPriceCents as number)}/mo
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums text-gray-900 dark:text-white" data-testid="billing-line-extras-amount">
                    {formatCentsAsUsd(extrasMonthlyCents)}
                  </td>
                </tr>
              )}

              {/* Metered usage — only when the usage summary is available */}
              {hasUsage && meteredUsageCents !== undefined && (
                <tr className="border-b border-gray-200 dark:border-gray-700" data-testid="billing-line-usage">
                  <td className="px-6 py-3 font-medium text-gray-900 dark:text-white">Metered usage</td>
                  <td className="px-6 py-3 text-gray-500 dark:text-gray-400">
                    {usage?.endpointCallCount ?? 0} endpoint call
                    {(usage?.endpointCallCount ?? 0) === 1 ? '' : 's'}
                    {rawUsageDollars !== undefined && (
                      <>
                        {' · '}
                        {formatUsd(rawUsageDollars)} LLM cost
                        {markup !== null && <> × {markup.toFixed(2)}</>}
                      </>
                    )}
                    {/* LLM usage is aggregated per calendar month, which is not
                        the Stripe billing period named in the header above — so
                        this row names the window it actually measures. */}
                    {usageWindowLabel && (
                      <span data-testid="billing-line-usage-window"> · {usageWindowLabel} usage</span>
                    )}
                    {spending.hasLimit && (
                      <>
                        {' · '}
                        <span data-testid="billing-line-usage-limit" className="font-medium text-gray-700 dark:text-gray-300">
                          {formatBilledCents(spending.billedCents)} of {formatSpendingCents(spending.limitCents as number)}{' '}
                          spending limit
                        </span>
                        {spendingPageEnabled && (
                          <>
                            {' · '}
                            <Link to={SPENDING_PAGE_PATH} className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
                              Spending
                            </Link>
                          </>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right tabular-nums text-gray-900 dark:text-white" data-testid="billing-line-usage-amount">
                    {formatUsd(billedUsageDollars as number)}
                  </td>
                </tr>
              )}

              {/* The spending bar under the metered-usage row (task 1159), only
                  with a limit set. Same colour rules as the Spending page. */}
              {hasUsage && meteredUsageCents !== undefined && spending.hasLimit && (
                <tr className="border-b border-gray-200 dark:border-gray-700" data-testid="billing-line-usage-spending">
                  <td colSpan={3} className="px-6 pb-3 pt-1">
                    <SpendingBar reading={spending} compact testId="billing-spending-bar" />
                  </td>
                </tr>
              )}

              {/* Key-scope split (task 1147) — informational, not summed. */}
              {hasUsage && keySpend && (
                <tr className="border-b border-gray-200 dark:border-gray-700" data-testid="billing-line-platform-key-usage">
                  <td className="py-2 pl-10 pr-6 text-gray-700 dark:text-gray-300">Platform key usage</td>
                  <td className="px-6 py-2 text-gray-500 dark:text-gray-400">
                    {formatUsd(keySpend.platformKeyDollars)} at list prices
                    {markup !== null && <> × {markup.toFixed(2)}</>} · what Innovate charges
                  </td>
                  <td
                    className="px-6 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300"
                    data-testid="billing-line-platform-key-usage-amount"
                  >
                    {formatUsd(keySpend.platformKeyDollars * (markup ?? 1))}
                  </td>
                </tr>
              )}
              {hasUsage && keySpend && hasAccountKey && (
                <tr className="border-b border-gray-200 dark:border-gray-700" data-testid="billing-line-account-key-usage">
                  <td className="py-2 pl-10 pr-6 text-gray-700 dark:text-gray-300">Your key usage (paid to provider)</td>
                  <td className="px-6 py-2 text-gray-500 dark:text-gray-400">
                    {formatUsd(keySpend.accountKeyDollars)} at catalog list prices · paid to your provider, not billed
                  </td>
                  <td
                    className="px-6 py-2 text-right tabular-nums text-gray-400 dark:text-gray-500"
                    data-testid="billing-line-account-key-usage-amount"
                  >
                    —
                  </td>
                </tr>
              )}

              {/* Estimated total */}
              <tr className="bg-gray-50 dark:bg-gray-900/40" data-testid="billing-line-total">
                <td className="px-6 py-3.5 font-semibold text-gray-900 dark:text-white" colSpan={2}>
                  Estimated total so far
                </td>
                <td
                  className="px-6 py-3.5 text-right text-base font-bold tabular-nums text-gray-900 dark:text-white"
                  data-testid="billing-line-total-amount"
                >
                  {estimatedTotalCents !== null ? formatCentsAsUsd(estimatedTotalCents) : 'Custom'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Entitlement override (task 942): AMS deliberately grants a plan other
            than the one Stripe bills. It is not a charge, so it is a line under
            the table rather than a priced row. The operator-facing override
            reason is NOT shown here. */}
        {isEntitlementOverride && entitlement && (
          <p
            className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300"
            data-testid="billing-entitlement-override"
          >
            Your entitlements are{' '}
            <span className="font-medium text-gray-800 dark:text-gray-100">{entitlement.name}</span>{' '}
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/60 dark:text-amber-200">
              override
            </span>{' '}
            — those limits and features are not what this account is billed for.
          </p>
        )}

        {!hasUsage && (
          <p className="px-6 py-3 text-xs text-gray-500 dark:text-gray-400" data-testid="billing-usage-pending-note">
            Usage-based lines (endpoint extras and metered usage) appear once this period's metering
            is available.
          </p>
        )}
      </section>

      {/* Endpoint allowance — meter + extras callout */}
      {hasUsage && hasFiniteEndpointCap && deployedEndpoints !== undefined && (
        <section
          className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
          data-testid="billing-endpoint-allowance-card"
        >
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Endpoint allowance</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-testid="billing-allowance-summary">
            {deployedEndpoints} of {includedEndpoints as number} included endpoint
            {(includedEndpoints as number) === 1 ? '' : 's'} deployed
            {extraEndpoints > 0
              ? ` — ${extraEndpoints} extra ${extraEndpoints === 1 ? 'is' : 'are'} being billed.`
              : '.'}
          </p>

          <div className="mt-4 h-2 w-full max-w-md overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              data-testid="billing-allowance-meter"
              className={`h-full rounded-full transition-all ${
                extraEndpoints > 0 ? 'bg-amber-500' : 'bg-indigo-500'
              }`}
              style={{ width: `${meterPct}%` }}
            />
          </div>

          {extraEndpoints > 0 && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-400" data-testid="billing-allowance-over-note">
              Undeploying an endpoint stops its extra charge at the next period.{' '}
              <Link
                to="/endpoints"
                data-testid="billing-view-endpoints-link"
                className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
              >
                View deployed endpoints
              </Link>
            </p>
          )}
        </section>
      )}

      {/* Invoice history — hidden entirely when the read is unavailable (null) */}
      {invoices !== null && (
        <section
          className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
          data-testid="billing-invoices-card"
        >
          <div className="p-6 pb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Invoices</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Every past bill, from Stripe. Receipts are sent to your billing email — see{' '}
              <Link to="/settings/payment" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
                Payment
              </Link>
              .
            </p>
          </div>

          {invoicesLoading && (
            <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400" data-testid="billing-invoices-loading">
              Loading your invoices…
            </p>
          )}

          {invoicesError && !invoicesLoading && (
            <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400" data-testid="billing-invoices-error">
              Your invoices are unavailable right now. Please try again shortly.
            </p>
          )}

          {!invoicesLoading && !invoicesError && invoices && invoices.length === 0 && (
            <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400" data-testid="billing-invoices-empty">
              No invoices yet. Your first invoice appears here after your first billing period closes.
            </p>
          )}

          {!invoicesLoading && !invoicesError && invoices && invoices.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="billing-invoices-table">
                <thead>
                  <tr className="border-y border-gray-200 bg-gray-50 text-left dark:border-gray-700 dark:bg-gray-900/40">
                    <th className="px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</th>
                    <th className="px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Invoice</th>
                    <th className="px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Period</th>
                    <th className="px-6 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Amount</th>
                    <th className="px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                    <th className="px-6 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="border-b border-gray-200 last:border-0 dark:border-gray-700"
                      data-testid={`billing-invoice-row-${invoice.id}`}
                    >
                      <td className="px-6 py-3 text-gray-900 dark:text-white">{formatDate(invoice.created)}</td>
                      <td className="px-6 py-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                        {invoice.number ?? '—'}
                      </td>
                      <td className="px-6 py-3 text-gray-500 dark:text-gray-400">{invoicePeriodLabel(invoice)}</td>
                      <td className="px-6 py-3 text-right tabular-nums text-gray-900 dark:text-white">
                        {formatMoney(invoice.amountDue, invoice.currency)}
                      </td>
                      <td className="px-6 py-3">
                        <InvoiceStatusBadge status={invoice.status} />
                      </td>
                      <td className="px-6 py-3 text-right">
                        {invoice.invoicePdfUrl || invoice.hostedInvoiceUrl ? (
                          <a
                            href={(invoice.invoicePdfUrl ?? invoice.hostedInvoiceUrl) as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`billing-invoice-pdf-${invoice.id}`}
                            className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                          >
                            PDF ↓
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
